import {
  IconCheck,
  IconHandFinger,
  IconClockQuestion,
  IconAlertCircle,
  Icon,
  IconCoins,
  IconReceipt,
  IconSettings,
  IconAlarm,
  IconClock,
  IconArchive,
  IconChartBar,
  IconReportMoney,
  IconCloudUpload,
  IconTag,
  IconClockDollar,
  IconBuildingBank,
  IconFileInvoice,
  IconCashBanknote,
} from "@tabler/icons-react";
import { CardProps } from "../components/Card";
import { MinimalCardProps } from "../components/MinimalCard";
import { BucketOption, IntentionStatus, PageScope } from "./types";
import { SUPPORTED_APP_CURRENCIES } from "../lib/currency";

export interface NavLinkProps {
  icon: Icon;
  label: string;
  url: string;
  prefix: string;
  id: number;
  scope: PageScope;
}

export const getPaymentStatusBadge = (paymentStatus: string) => {
  const normalizedStatus = paymentStatus.toLowerCase();

  if (normalizedStatus === "paid") {
    return {
      label: "Paid",
      className: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    };
  }

  if (normalizedStatus === "open") {
    return {
      label: "Open",
      className: "bg-amber-100 text-amber-700 ring-amber-200",
    };
  }

  if (normalizedStatus === "closed") {
    return {
      label: "Closed",
      className: "bg-slate-100 text-slate-700 ring-slate-200",
    };
  }

  const fallbackLabel = paymentStatus
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return {
    label: fallbackLabel,
    className: "bg-gray-100 text-gray-700 ring-gray-200",
  };
};

/** Main nav (Figma order). */
export const navbarPrimary: NavLinkProps[] = [
  {
    id: 1,
    icon: IconChartBar,
    label: "Dashboard",
    url: "/",
    prefix: "/",
    scope: "DASHBOARDS",
  },
  {
    id: 3,
    icon: IconChartBar,
    label: "Analytics",
    url: "/analytics",
    prefix: "/analytics",
    scope: "ANALYTICS",
  },
  {
    id: 2,
    icon: IconClockDollar,
    label: "Receivables",
    url: "/receivables",
    prefix: "/receivables",
    scope: "RECEIVABLES",
  },
  {
    id: 6,
    icon: IconReportMoney,
    label: "Cashflow",
    url: "/cashflow",
    prefix: "/cashflow",
    scope: "CASHFLOW",
  },
  {
    id: 13,
    icon: IconChartBar,
    label: "Forecast",
    url: "/forecast",
    prefix: "/forecast",
    scope: "FORECAST",
  },
  {
    id: 10,
    icon: IconReceipt,
    label: "Transactions",
    url: "/transactions",
    prefix: "/transactions",
    scope: "TRANSACTIONS",
  },
  {
    id: 12,
    icon: IconTag,
    label: "Categories",
    url: "/categories",
    prefix: "/categories",
    scope: "TRANSACTIONS",
  },
  {
    id: 15,
    icon: IconFileInvoice,
    label: "Supplier\nInvoices",
    url: "/supplier-invoices",
    prefix: "/supplier-invoices",
    scope: "SUPPLIER_INVOICES",
  },
  {
    id: 16,
    icon: IconCashBanknote,
    label: "Invoices for\nPayment",
    url: "/invoices-for-payment",
    prefix: "/invoices-for-payment",
    scope: "SUPPLIER_INVOICES",
  },
  {
    id: 14,
    icon: IconBuildingBank,
    label: "Bank\nReconciliation",
    url: "/bank-reconciliation",
    prefix: "/bank-reconciliation",
    scope: "BANK_RECONCILIATION",
  },
  {
    id: 9,
    icon: IconCloudUpload,
    label: "Files &\nIntegrations",
    url: "/files-integrations",
    prefix: "/files-integrations",
    scope: "FILES_INTEGRATIONS",
  },
];

/** Bottom nav (Archive, Settings). */
export const navbarFooter: NavLinkProps[] = [
  {
    id: 4,
    icon: IconArchive,
    label: "Archive",
    url: "/archive",
    prefix: "/archive",
    scope: "ARCHIVE",
  },
  {
    id: 5,
    icon: IconSettings,
    label: "Settings",
    url: "/settings",
    prefix: "/settings",
    scope: "SETTINGS",
  },
];

export const intentionStatusCardOrder: IntentionStatus[] = [
  "Approved",
  "In_progress",
  "Unresolved",
  "No_reply",
  "Needs_attention",
];

export const intentionStatusData: Record<
  IntentionStatus,
  { cardColor: CardProps["color"]; tableIconColor: string; icon: Icon }
> = {
  Approved: {
    cardColor: "lime",
    tableIconColor: "green",
    icon: IconCheck,
  },
  Unresolved: {
    cardColor: "yellow",
    tableIconColor: "yellow",
    icon: IconHandFinger,
  },
  No_reply: {
    cardColor: "orange",
    tableIconColor: "orange",
    icon: IconClockQuestion,
  },
  Needs_attention: {
    cardColor: "red",
    tableIconColor: "red",
    icon: IconAlertCircle,
  },
  In_progress: {
    cardColor: "lavender",
    tableIconColor: "gray",
    icon: IconClock,
  },
};

export const invoicesMinimalCardData: MinimalCardProps[] = [
  {
    heading: "Unpaid Invoices",
    description: "?",
    Icon: IconReceipt,
    key: 1,
  },
  {
    heading: "Open Balance",
    description: "?",
    Icon: IconCoins,
    key: 2,
  },
  {
    heading: "Overdue",
    description: "?",
    Icon: IconAlarm,
    key: 3,
  },
];

export const passwordErrorMsg =
  "At least 8 characters, including one lowercase, one uppercase, and one symbol (!@#$%^&*()).";

export const roles = ["USER"];

export const quickPrompts = [
  {
    label: "More friendly",
    prompt: "Make the email more friendly and warm",
  },
  {
    label: "More professional",
    prompt: "Make the email more professional",
  },
  {
    label: "More firm",
    prompt: "Make the email more firm and direct",
  },
  {
    label: "Final notice",
    prompt: "Make this a final notice email",
  },
];

export const replyQuickPrompts = [
  {
    label: "Draft reply",
    prompt: "Write a clear, professional reply to the customer's message.",
  },
  {
    label: "More friendly",
    prompt: "Make the reply more friendly and warm.",
  },
  {
    label: "More professional",
    prompt: "Make the reply more professional and polished.",
  },
  {
    label: "Payment reminder",
    prompt:
      "Write a polite payment follow-up that references any open invoice(s).",
  },
];

export const customersMinimalCardData: MinimalCardProps[] = [
  {
    heading: "Customers with Open Balances",
    description: "?",
    Icon: IconReceipt,
    key: 1,
  },
  {
    heading: "Open Balance",
    description: "?",
    Icon: IconCoins,
    key: 2,
  },
  {
    heading: "Overdue",
    description: "?",
    Icon: IconAlarm,
    key: 3,
  },
  // {
  //   heading: "Avg. Invoice to Cash",
  //   description: "?",
  //   Icon: Clock,
  //   key:4,
  // },
];

export const bucketLabelMap: Record<BucketOption, string> = {
  next_7_days: "Next 7 Days",
  next_30_days: "Next 30 Days",
  next_year: "Next Year",
};

export const emailContentSanitizeConfig = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "a",
    "ul",
    "ol",
    "li",
    "div",
    "span",
    "table",
    "thead",
    "tbody",
    "tr",
    "td",
    "th",
    "img",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "blockquote",
    "hr",
  ],
  ALLOWED_ATTR: [
    "href",
    "target",
    "rel",
    "src",
    "alt",
    "style",
    "class",
    "width",
    "height",
  ],
};

export const autoSendOptions = [
  {
    value: "AutoSendTemplatesOnly",
    label: "Auto-send template drafts",
  },
  {
    label: "Never auto-send drafts",
    value: "Never",
  },
];

export const languageOptions = [
  { value: "Hebrew", label: "Hebrew" },
  { value: "English", label: "English" },
  { value: "Chinese", label: "Chinese" },
] as const;

/** ISO codes for collection performance currency filter (NIS is merged into ILS server-side). */
export const COLLECTION_PERFORMANCE_CURRENCY_OPTIONS = [
  ...SUPPORTED_APP_CURRENCIES,
] as const;

/** Keep historical dashboard default for collection performance. */
export const COLLECTION_PERFORMANCE_DEFAULT_CURRENCY = "ILS" as const;

export type CollectionPerformanceCurrency =
  (typeof COLLECTION_PERFORMANCE_CURRENCY_OPTIONS)[number];
